/**
 * The rules `POST /tenants/register` enforces, restated on this side of the wire.
 *
 * WHY RESTATE THEM AT ALL. The route is rate limited to five calls an hour per
 * IP (`TenantsController.register`). A form that discovers its own mistakes by
 * posting them spends that budget on typos: four fumbled attempts is the entire
 * allowance, and the fifth — the one that would have worked — gets a 429. So the
 * cheap, certain refusals happen here, and the network is only asked the one
 * question it alone can answer: is this web address free.
 *
 * These are a MIRROR, never the authority. The server validates independently
 * and its answer wins; if the two ever disagree, the server's message is what
 * gets rendered. Kept deliberately in one file with no React in it so the rules
 * can be tested as arithmetic rather than through a form.
 *
 * Sources, so a future edit knows what it has to match:
 *   - slug shape        `RegisterTenantDto.slug` and `slugFormatError()`
 *   - slug length 3..40 `SLUG_MIN_LENGTH` / `SLUG_MAX_LENGTH`
 *   - all-digits        `slugFormatError()` only — the DTO regex admits `123`,
 *                       the service then refuses it. Catching it here turns a
 *                       confusing 400 into an inline hint.
 *   - password          `passwordPolicyError()` in `common/validators`
 */

export const SLUG_MIN_LENGTH = 3;
export const SLUG_MAX_LENGTH = 40;
export const PASSWORD_MIN_LENGTH = 12;
export const PASSWORD_MAX_BYTES = 72;

/** i18n keys, so a caller can render the reason in the reader's language. */
export type RegistrationErrorKey =
  | 'venueNameRequired'
  | 'slugRequired'
  | 'slugLength'
  | 'slugFormat'
  | 'slugDigits'
  | 'firstNameRequired'
  | 'emailRequired'
  | 'emailFormat'
  | 'passwordLength'
  | 'passwordBytes'
  | 'passwordClasses'
  | 'passwordSpaces';

/**
 * A venue name turned into a candidate web address.
 *
 * `NFD` + stripping combining marks folds `Café` to `Cafe` rather than dropping
 * the accented letter, which is the difference between `caf-` and `cafe` for a
 * great many real venue names. Everything else non-alphanumeric collapses to a
 * single dash, and leading/trailing dashes go — the three things the shape rule
 * refuses. Truncation trims to the last dash boundary so the suggestion is never
 * a word cut in half.
 */
export function slugify(name: string): string {
  const base = name
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
  if (base.length <= SLUG_MAX_LENGTH) return base;
  const cut = base.slice(0, SLUG_MAX_LENGTH);
  const lastDash = cut.lastIndexOf('-');
  return (lastDash > 0 ? cut.slice(0, lastDash) : cut).replace(/-+$/g, '');
}

export function slugError(slug: string): RegistrationErrorKey | null {
  if (!slug) return 'slugRequired';
  if (slug.length < SLUG_MIN_LENGTH || slug.length > SLUG_MAX_LENGTH) return 'slugLength';
  if (!/^[a-z0-9]+(-[a-z0-9]+)*$/.test(slug)) return 'slugFormat';
  // The service refuses this after the DTO regex has already accepted it.
  if (/^[0-9]+$/.test(slug)) return 'slugDigits';
  return null;
}

/** How many of lowercase / uppercase / digit / symbol the password uses. */
export function passwordClasses(value: string): number {
  return [/[a-z]/, /[A-Z]/, /[0-9]/, /[^A-Za-z0-9]/].filter((re) => re.test(value)).length;
}

/**
 * BYTES, not characters, for the upper bound — bcrypt truncates at 72 bytes and
 * an Arabic or emoji passphrase reaches that in far fewer characters. A user
 * whose password is silently shortened has a wrong model of their own security.
 */
export function passwordByteLength(value: string): number {
  return new TextEncoder().encode(value).length;
}

export function passwordError(value: string): RegistrationErrorKey | null {
  if (value.length < PASSWORD_MIN_LENGTH) return 'passwordLength';
  if (passwordByteLength(value) > PASSWORD_MAX_BYTES) return 'passwordBytes';
  if (value.trim().length !== value.length) return 'passwordSpaces';
  if (passwordClasses(value) < 3) return 'passwordClasses';
  return null;
}

/**
 * Deliberately permissive. The server runs `@IsEmail()` and is the authority;
 * this only catches the shapes that are obviously not an address, because a
 * strict client-side pattern's failure mode is rejecting a real mailbox nobody
 * can then sign up with.
 */
export function emailError(value: string): RegistrationErrorKey | null {
  if (!value) return 'emailRequired';
  if (!/^[^\s@]+@[^\s@.]+(\.[^\s@.]+)+$/.test(value) || value.length > 254) return 'emailFormat';
  return null;
}

export interface RegistrationForm {
  name: string;
  slug: string;
  firstName: string;
  lastName: string;
  email: string;
  password: string;
}

export type RegistrationErrors = Partial<Record<keyof RegistrationForm, RegistrationErrorKey>>;

/** Field order matters: it decides which input gets focus when the form refuses. */
export const FIELD_ORDER: (keyof RegistrationForm)[] = [
  'name',
  'slug',
  'firstName',
  'lastName',
  'email',
  'password',
];

export function validateRegistration(form: RegistrationForm): RegistrationErrors {
  const errors: RegistrationErrors = {};
  if (!form.name.trim()) errors.name = 'venueNameRequired';
  const slug = slugError(form.slug.trim().toLowerCase());
  if (slug) errors.slug = slug;
  if (!form.firstName.trim()) errors.firstName = 'firstNameRequired';
  const email = emailError(form.email.trim().toLowerCase());
  if (email) errors.email = email;
  const password = passwordError(form.password);
  if (password) errors.password = password;
  return errors;
}

/**
 * The request body, normalized the way the DTO's `@Transform`s would normalize
 * it anyway. Doing it here means what the user sees echoed back — the slug
 * preview, the email on the "we created it" panel — is the value the server
 * actually stored, not the one they typed.
 *
 * `lastName` is omitted rather than sent empty: the column is nullable and
 * `@IsOptional()` skips an absent key, where `''` would store a blank surname.
 *
 * `locale` and `timezone` are the browser's, because they are known facts about
 * this person and asking for them on the one form that must have no friction is
 * the wrong trade. No `currency` — it fixes money precision for the tenant and
 * `TenantsService` deliberately refuses to guess it, so neither do we.
 */
export function toRegisterBody(
  form: RegistrationForm,
  context: { locale: string; timezone?: string },
) {
  const lastName = form.lastName.trim();
  return {
    name: form.name.trim(),
    slug: form.slug.trim().toLowerCase(),
    locale: context.locale.slice(0, 2).toLowerCase(),
    ...(context.timezone ? { timezone: context.timezone } : {}),
    owner: {
      email: form.email.trim().toLowerCase(),
      password: form.password,
      firstName: form.firstName.trim(),
      ...(lastName ? { lastName } : {}),
    },
  };
}

/** The browser's IANA zone, or nothing — a guess here would be a silent lie. */
export function browserTimezone(): string | undefined {
  try {
    const zone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return zone && zone.length <= 64 ? zone : undefined;
  } catch {
    return undefined;
  }
}

export interface RegistrationFailure {
  /** i18n key under `auth.register.errors`, or null when `message` is verbatim. */
  key: 'slugUnavailable' | 'rateLimited' | 'network' | 'generic' | null;
  /** The server's own words, when it gave usable ones. */
  message: string | null;
  /** Set when the failure belongs against one input rather than the form. */
  field: keyof RegistrationForm | null;
}

/**
 * An axios rejection turned into something a person can act on.
 *
 * The three cases that need a human sentence rather than a raw error:
 *
 *   409 — the address is unavailable. A RESERVED name and a TAKEN name answer
 *         identically on purpose (`SLUG_UNAVAILABLE`), so that a stranger cannot
 *         use the signup form to enumerate which venues exist. The copy must not
 *         claim to know which of the two it was, because this client genuinely
 *         does not know.
 *   429 — the five-per-hour throttle. Rendered raw this is a bare status code on
 *         a page that just refused to create an account, which reads as a bug.
 *   no response — the request never arrived, so nothing was created. Worth
 *         saying, because it is the one failure where retrying is certainly safe.
 *
 * A 400 is the server's validation, and its `message` (an array from the global
 * pipe) is more specific than anything this file could substitute, so it is
 * passed through verbatim.
 */
export function describeRegistrationFailure(error: unknown): RegistrationFailure {
  const response = (
    error as { response?: { status?: number; data?: { message?: unknown } } } | undefined
  )?.response;

  if (!response) return { key: 'network', message: null, field: null };

  const raw = response.data?.message;
  const message = Array.isArray(raw)
    ? raw.filter((m): m is string => typeof m === 'string').join(' ')
    : typeof raw === 'string'
      ? raw
      : null;

  if (response.status === 409) return { key: 'slugUnavailable', message: null, field: 'slug' };
  if (response.status === 429) return { key: 'rateLimited', message: null, field: null };
  if (response.status === 400 && message) return { key: null, message, field: null };
  return { key: 'generic', message: null, field: null };
}
